const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function createGoogleAnalyticsService(
  props: {
    measurementId: string;
  } = {
    measurementId: GA_MEASUREMENT_ID!,
  },
) {
  const measurementId = props.measurementId || GA_MEASUREMENT_ID;

  if (!measurementId) {
    throw new Error(
      'GA_MEASUREMENT_ID is not set. Please set the environment variable NEXT_PUBLIC_GA_MEASUREMENT_ID.',
    );
  }

  return new GoogleAnalyticsService(measurementId);
}

class GoogleAnalyticsService {
  private initialized = false;

  constructor(private readonly measurementId: string) {}

  async initialize() {
    if (this.initialized) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    // Must use `arguments` (not rest params) so gtag.js recognizes the entries
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId);

    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    script.async = true;
    document.head.appendChild(script);

    this.initialized = true;
  }

  async trackPageView(path: string) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: path,
    });
  }

  async trackEvent(
    eventName: string,
    eventProperties: Record<string, string | string[]> = {},
  ) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    window.gtag('event', eventName, eventProperties);
  }

  async identify(userId: string, traits: Record<string, string> = {}) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    window.gtag('set', 'user_properties', {
      user_id: userId,
      ...traits,
    });
  }
}
